import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ShoppingBag, MapPin, ChevronDown, Sparkles, Search, Heart } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useMall } from '@/lib/mall';
import { useCart } from '@/lib/cart';
import CartDrawer from './CartDrawer';
import ConciergeWidget from './ConciergeWidget';
import MarqueeRibbon from './MarqueeRibbon';

const NAV = [
    { to: '/shop', label: 'Shop' },
    { to: '/brands', label: 'Brands' },
    { to: '/plan', label: 'Plan Visit' },
    { to: '/mall-pass', label: 'Mall Pass' },
    { to: '/offers', label: 'Offers' },
    { to: '/rewards', label: 'Rewards' },
];

export default function Layout({ children }) {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [mallPickerOpen, setMallPickerOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { user, type, logout } = useAuth();
    const { selectedMall, allMalls, choose, clear } = useMall();
    const { cart, setOpen: setCartOpen } = useCart();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => { setMobileOpen(false); setMallPickerOpen(false); }, [location.pathname]);

    const handleLogout = () => { logout(); navigate('/'); };
    const overHero = location.pathname === '/' && !scrolled;
    // Color tokens that flip based on whether header sits over the dark blue hero
    const navText = overHero ? 'text-white hover:text-inorbit-yellow' : 'text-inorbit-blue hover:text-inorbit-orange';
    const navActive = overHero ? 'text-inorbit-yellow' : 'text-inorbit-orange';
    const iconText = overHero ? 'text-white hover:text-inorbit-yellow' : 'text-inorbit-blue hover:text-inorbit-orange';
    const mallPillCls = overHero
        ? 'pill border-2 border-white/40 text-white hover:bg-white hover:text-inorbit-blue hover:border-white'
        : 'pill pill-outline hover:bg-inorbit-blue hover:text-white hover:border-inorbit-blue';

    return (
        <div className="App min-h-screen flex flex-col page-in">
            {/* Editorial film grain — global */}
            <div className="noise-overlay" aria-hidden="true" />

            <header
                className={`sticky top-0 z-40 transition-all duration-300 ${overHero ? 'bg-transparent' : 'glass-header'}`}
                data-testid="site-header"
            >
                <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between h-20 gap-4">
                    <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-home-link">
                        <img src="/inorbit-logo.png" alt="Inorbit" className={`h-10 w-auto ${overHero ? 'bg-white/95 p-1 rounded-sm' : ''}`} />
                        <div className="hidden sm:block">
                            <div className={`text-[10px] tracking-[0.3em] font-bold ${overHero ? 'text-inorbit-yellow' : 'text-inorbit-orange'}`}>ONE</div>
                        </div>
                    </Link>

                    {/* Mall pill */}
                    <button
                        onClick={() => setMallPickerOpen(true)}
                        data-testid="mall-picker-btn"
                        className={`${mallPillCls} transition-colors shrink-0`}
                    >
                        <MapPin size={12} strokeWidth={2} />
                        {selectedMall ? selectedMall.short_name : 'Choose Mall'}
                        <ChevronDown size={12} strokeWidth={2} />
                    </button>

                    <nav className="hidden lg:flex items-center gap-8 ml-2">
                        {NAV.map((n) => (
                            <Link
                                key={n.to}
                                to={n.to}
                                data-testid={`nav-${n.label.toLowerCase()}`}
                                className={`text-[13px] tracking-[0.12em] uppercase font-semibold transition-colors ${location.pathname.startsWith(n.to) ? navActive : navText}`}
                            >
                                {n.label}
                            </Link>
                        ))}
                    </nav>

                    <div className="hidden md:flex items-center gap-3 ml-auto">
                        <Link to="/concierge" className={`${iconText} transition-colors p-2`} data-testid="nav-concierge" title="AI Concierge">
                            <Sparkles size={20} strokeWidth={1.8} />
                        </Link>
                        <Link to="/wishlist" className={`${iconText} transition-colors p-2`} data-testid="nav-wishlist" title="Wishlist">
                            <Heart size={20} strokeWidth={1.8} />
                        </Link>
                        <button onClick={() => setCartOpen(true)} className={`relative ${iconText} p-2`} data-testid="cart-btn">
                            <ShoppingBag size={20} strokeWidth={1.8} />
                            {cart.item_count > 0 && (
                                <span className="absolute -top-1 -right-1 bg-inorbit-orange text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                                    {cart.item_count}
                                </span>
                            )}
                        </button>
                        {type === 'customer' && user ? (
                            <div className="relative group">
                                <button data-testid="user-menu-btn" className={`flex items-center gap-2 p-1 ${overHero ? 'text-white' : 'text-inorbit-blue'}`}>
                                    <div className={`w-9 h-9 flex items-center justify-center text-xs font-bold uppercase rounded-full ${overHero ? 'bg-inorbit-yellow text-inorbit-blue' : 'bg-inorbit-blue text-inorbit-yellow'}`}>
                                        {user.name?.[0] || 'U'}
                                    </div>
                                </button>
                                <div className="absolute right-0 top-full mt-2 w-60 bg-white border border-border-subtle opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all shadow-xl rounded-md overflow-hidden">
                                    <div className="px-5 py-4 border-b border-border-subtle bg-inorbit-gradient text-white">
                                        <div className="text-sm font-semibold">{user.name}</div>
                                        <div className="text-xs opacity-80 mt-1">{user.membership_id}</div>
                                        <div className="text-xs mt-2 flex items-center gap-2">
                                            <span className="pill pill-yellow text-[10px] py-0.5">{user.tier}</span>
                                            <span className="opacity-90">{user.points_balance} pts</span>
                                        </div>
                                    </div>
                                    <Link to="/profile" className="block px-5 py-3 text-sm text-inorbit-blue hover:bg-cream" data-testid="menu-profile">My Profile</Link>
                                    <Link to="/loyalty" className="block px-5 py-3 text-sm text-inorbit-blue hover:bg-cream" data-testid="menu-loyalty">Loyalty & Wallet</Link>
                                    <Link to="/orders" className="block px-5 py-3 text-sm text-inorbit-blue hover:bg-cream" data-testid="menu-orders">My Orders</Link>
                                    <Link to="/bills" className="block px-5 py-3 text-sm text-inorbit-blue hover:bg-cream" data-testid="menu-bills">Upload Bill</Link>
                                    <Link to="/games" className="block px-5 py-3 text-sm text-inorbit-blue hover:bg-cream" data-testid="menu-games">Play & Win</Link>
                                    <button onClick={handleLogout} className="block w-full text-left px-5 py-3 text-sm text-inorbit-blue hover:bg-cream border-t border-border-subtle" data-testid="menu-logout">Sign out</button>
                                </div>
                            </div>
                        ) : (
                            <Link to="/login" className={`text-[11px] tracking-[0.18em] uppercase font-semibold pb-1 border-b-2 transition-colors ${overHero ? 'text-white border-inorbit-yellow hover:text-inorbit-yellow' : 'text-inorbit-blue border-inorbit-yellow hover:text-inorbit-orange'}`} data-testid="nav-login">Sign In</Link>
                        )}
                    </div>

                    <button className={`lg:hidden ${overHero ? 'text-white' : 'text-inorbit-blue'}`} onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
                        <Menu size={26} strokeWidth={1.8} />
                    </button>
                </div>
            </header>

            {/* Mall picker overlay */}
            {mallPickerOpen && (
                <div className="fixed inset-0 z-50 bg-inorbit-blue-deeper/90 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setMallPickerOpen(false)} data-testid="mall-picker-overlay">
                    <div className="bg-white max-w-5xl w-full max-h-[90vh] overflow-y-auto rounded-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="p-8 border-b border-border-subtle flex items-center justify-between">
                            <div>
                                <div className="overline">Choose Your Mall</div>
                                <h2 className="font-serif text-3xl text-inorbit-blue mt-1">Where shall we shop today?</h2>
                            </div>
                            <button onClick={() => setMallPickerOpen(false)} data-testid="mall-picker-close"><X size={24} /></button>
                        </div>
                        <div className="p-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {allMalls.map((m) => (
                                <button
                                    key={m.id}
                                    onClick={() => {
                                        choose(m.slug);
                                        setMallPickerOpen(false);
                                        // If we're already on a mall-specific page (e.g. /mall/malad),
                                        // navigate to the new slug so the page reloads with that mall's data.
                                        // Otherwise stay on the current page (Home/Shop/etc. already react
                                        // to the context change).
                                        if (location.pathname.startsWith('/mall/')) {
                                            navigate(`/mall/${m.slug}`);
                                        }
                                    }}
                                    className={`text-left card-luxury overflow-hidden img-zoom ${selectedMall?.slug === m.slug ? 'border-inorbit-orange ring-2 ring-inorbit-orange' : ''}`}
                                    data-testid={`mall-picker-${m.slug}`}
                                >
                                    <div className="aspect-[4/3] overflow-hidden">
                                        <img src={m.hero_image} alt={m.name} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="p-5">
                                        <div className="overline text-inorbit-orange mb-1">{m.city}</div>
                                        <div className="font-serif text-xl text-inorbit-blue">{m.short_name}</div>
                                        <div className="text-xs text-text-tertiary mt-1">{m.brand_count}+ brands • {m.tagline}</div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {mobileOpen && (
                <div className="fixed inset-0 z-50 bg-inorbit-blue text-white flex flex-col lg:hidden" data-testid="mobile-menu-overlay">
                    <div className="flex items-center justify-between px-6 h-20 border-b border-white/15">
                        <img src="/inorbit-logo.png" alt="Inorbit" className="h-10 w-auto bg-white p-1 rounded" />
                        <button onClick={() => setMobileOpen(false)} data-testid="mobile-close-btn"><X size={28} /></button>
                    </div>
                    <div className="flex-1 px-6 py-10 flex flex-col gap-6">
                        {NAV.map((n) => (
                            <Link key={n.to} to={n.to} className="font-serif text-4xl">{n.label}</Link>
                        ))}
                        <Link to="/concierge" className="font-serif text-4xl text-inorbit-yellow">Concierge</Link>
                        <Link to="/loyalty" className="font-serif text-4xl">Loyalty</Link>
                        <Link to="/bills" className="font-serif text-3xl text-inorbit-yellow/80">Upload Bill</Link>
                        <Link to="/games" className="font-serif text-3xl text-inorbit-yellow/80">Play & Win</Link>
                        <div className="mt-auto pt-6 border-t border-white/15">
                            {type === 'customer' && user ? (
                                <>
                                    <div className="mb-3">{user.name} • {user.points_balance} pts</div>
                                    <button onClick={handleLogout} className="text-inorbit-yellow">Sign Out</button>
                                </>
                            ) : (
                                <>
                                    <Link to="/login" className="btn-yellow block text-center">Sign In</Link>
                                    <Link to="/register" className="block text-center mt-3 text-inorbit-yellow">Create Account</Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <CartDrawer />
            <ConciergeWidget />

            <main className="flex-1">{children}</main>

            <MarqueeRibbon testId="footer-marquee" />

            <footer className="bg-inorbit-blue text-white relative" data-testid="site-footer">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-12">
                        <div className="md:col-span-2">
                            <img src="/inorbit-logo.png" alt="Inorbit" className="h-14 w-auto bg-white p-2 rounded mb-6 inline-block" />
                            <p className="text-sm text-white/70 leading-relaxed max-w-sm">
                                One mall identity. One wallet. One commerce. Shop 500+ brands across Inorbit Mumbai, Navi Mumbai, Hyderabad, Vadodara, Hubballi and Visakhapatnam.
                            </p>
                            <div className="script-tag text-inorbit-yellow text-2xl mt-4">Live an Experience</div>
                        </div>
                        <div>
                            <div className="overline text-inorbit-yellow mb-4">Shop</div>
                            <ul className="space-y-3 text-sm">
                                <li><Link to="/shop" className="hover:text-inorbit-yellow">All Products</Link></li>
                                <li><Link to="/brands" className="hover:text-inorbit-yellow">Brands</Link></li>
                                <li><Link to="/offers" className="hover:text-inorbit-yellow">Offers</Link></li>
                                <li><Link to="/events" className="hover:text-inorbit-yellow">Events</Link></li>
                                <li><Link to="/rewards" className="hover:text-inorbit-yellow">Rewards</Link></li>
                            </ul>
                        </div>
                        <div>
                            <div className="overline text-inorbit-yellow mb-4">Members</div>
                            <ul className="space-y-3 text-sm">
                                <li><Link to="/loyalty" className="hover:text-inorbit-yellow">Loyalty Programme</Link></li>
                                <li><Link to="/bills" className="hover:text-inorbit-yellow">Upload Bill</Link></li>
                                <li><Link to="/games" className="hover:text-inorbit-yellow">Play & Win</Link></li>
                                <li><Link to="/orders" className="hover:text-inorbit-yellow">My Orders</Link></li>
                                <li><Link to="/concierge" className="hover:text-inorbit-yellow">AI Concierge</Link></li>
                            </ul>
                        </div>
                        <div>
                            <div className="overline text-inorbit-yellow mb-4">Business</div>
                            <ul className="space-y-3 text-sm">
                                <li><Link to="/admin/login" className="hover:text-inorbit-yellow">Admin Login</Link></li>
                                <li><Link to="/brand/login" className="hover:text-inorbit-yellow">Brand Portal</Link></li>
                                <li><a href="/inorbit-one-proposal.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-inorbit-yellow inline-flex items-center gap-1.5" data-testid="footer-proposal-link">Strategic Brief <span className="text-[9px] text-inorbit-yellow/70">PDF</span></a></li>
                                <li><a href="mailto:leasing@inorbit.in" className="hover:text-inorbit-yellow">Leasing</a></li>
                                <li><a href="mailto:corporate.communications@inorbit.in" className="hover:text-inorbit-yellow">Partnerships</a></li>
                                <li><a href="tel:18001232994" className="hover:text-inorbit-yellow">1800 123 2994</a></li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-white/15 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-white/50">
                        <div>© 2026 Inorbit Malls — A K Raheja Corp company. All rights reserved.</div>
                        <div>Powered by <span className="text-inorbit-yellow font-semibold">Fundle.ai</span></div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
